import { Phone, ArrowUpRight, Shield, Clock, MapPin } from "lucide-react";
import { BRAND, TRUST_BADGES } from "@/lib/brand";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_transport-hub-345/artifacts/3n3iv3ni_Screenshot_20260609_112234_Gallery.jpg";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#050505]"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Luxury car being loaded onto an enclosed transporter"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      </div>

      {/* Grid lines decoration */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.08]">
        <div className="h-full w-full" style={{
          backgroundImage:
            "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }} />
      </div>

      <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 pt-40 pb-24 lg:pt-48 lg:pb-32 min-h-screen flex flex-col justify-between">
        {/* Top eyebrow */}
        <div className="flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-[#E60000] font-bold fade-up">
          <span className="w-8 h-px bg-[#E60000]" />
          Recovery · Transport · {BRAND.coverage}
        </div>

        {/* Main copy */}
        <div className="mt-12 max-w-5xl fade-up" style={{ animationDelay: "0.1s" }}>
          <h1 className="font-display font-extrabold text-white text-5xl sm:text-6xl lg:text-8xl xl:text-9xl leading-[0.95] tracking-tight">
            Professional
            <br />
            <span className="text-stroke">Vehicle</span>
            <br />
            Transport &amp; Recovery
          </h1>

          <p className="mt-8 max-w-2xl text-zinc-300 text-base lg:text-xl leading-relaxed">
            Get your instant vehicle transport quote — door-to-door recovery
            and prestige car transport across the UK and into Europe. Book
            in minutes, 24/7, fully insured.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="/quote"
              data-testid="hero-get-quote-button"
              className="btn-primary"
            >
              Get an Instant Quote
              <ArrowUpRight className="w-5 h-5" />
            </a>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              data-testid="hero-call-now-button"
              className="btn-ghost"
            >
              <Phone className="w-5 h-5" />
              Call {BRAND.phone}
            </a>
          </div>
        </div>

        {/* Bottom trust strip */}
        <div className="mt-16 lg:mt-24 fade-up" style={{ animationDelay: "0.25s" }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            <TrustCell icon={<Clock className="w-4 h-4" />} label="24 / 7 Callout" sub="365 days a year" />
            <TrustCell icon={<Shield className="w-4 h-4" />} label="Fully Insured" sub="Goods in transit" />
            <TrustCell icon={<MapPin className="w-4 h-4" />} label="UK & Europe" sub="Burton based" />
            <TrustCell icon={<ArrowUpRight className="w-4 h-4" />} label="Prestige Care" sub="Soft strap & nets" />
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 right-6 hidden md:flex flex-col items-center text-zinc-500 text-[10px] tracking-[0.3em] uppercase rotate-180" style={{ writingMode: "vertical-rl" }}>
        Scroll · Explore
      </div>
    </section>
  );
}

function TrustCell({ icon, label, sub }) {
  return (
    <div className="bg-black/70 backdrop-blur-sm px-5 py-5 flex items-start gap-3">
      <div className="text-[#E60000] mt-0.5">{icon}</div>
      <div>
        <div className="text-white text-sm font-bold tracking-wide">{label}</div>
        <div className="text-zinc-500 text-xs mt-0.5">{sub}</div>
      </div>
    </div>
  );
}
